import { Injectable } from '@angular/core'
import { environment } from 'src/environments/environment'

@Injectable()
export class AppConfig {
  ht_protocol = window.location.protocol
  ws_protocol = this.ht_protocol === 'http:' ? 'ws:' : 'wss:'
  hostname = window.location.hostname
  is_localhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(this.hostname)
  project = this.is_localhost ? 'dev' : this.hostname.split('.')[0]
  api_prefix = this.is_localhost ? '' : environment.backendPrefix
  port = environment.backendPort

  base_websocket = `${this.ws_protocol}//${this.api_prefix}${this.hostname}:${this.port}/dashboard/v1/1`
  websocket_use_here_param = 'use_here'
  websocket_close_code_multiple_connections = 4001
  base_url = `${this.ht_protocol}//${this.api_prefix}${this.hostname}:${this.port}/api/v1/`

  release_info_file_url = './version.txt'
  social_account_login_url = {
    google: `${this.ht_protocol}//${this.hostname}:${this.port}/accounts/google/login/?process=login`,
    facebook: `${this.ht_protocol}//${this.hostname}:${this.port}/accounts/facebook/login/?process=login`,
  } as const
  api_urls = {
    centers: this.base_url + 'flightnetworks/centers/',
    users: this.base_url + 'users/',
    vehicles: this.base_url + 'vehicles/',
    vehicles_maintenance: this.base_url + 'vehicles_maintenance/',
    discrepancy_reports: this.base_url + 'discrepancy_reports/',
    checklists: this.base_url + 'checklists/',
    flightnetworks: this.base_url + 'flightnetworks/',
    version: this.base_url + 'version/',
    part_numbers: this.base_url + 'part_numbers/',
    history: this.base_url + 'history/',
    part_instances: this.base_url + 'part_instances/',
    manual: this.base_url + 'manual/',
    audit_logs: this.base_url + 'audit_logs/',
    checklist_reports: this.base_url + 'checklist_reports/',
    time_and_duty_reports: this.base_url + 'time_and_duty_report/',
    mission_note: this.base_url + 'mission_notes/',
  }
  config = {
    name: 'Flytrex',
    /**
     * Constants for google maps, used in this app
     */
    googleMap: {
      /**
       * Used to exclude points from bounds which are too far from center
       */
      defaultMaximumBoundsRadius: 4000,
      /**
       * Defines N-number and we will take every N-th point from
       * reported route to draw it
       */
      reportedRouteEveryNthPointSelector: 1,
    },
    server_api: {
      centers: {
        add: this.api_urls.centers,
        edit: this.api_urls.centers,
        get: this.api_urls.centers,
        delete: this.api_urls.centers,
        get_center_timezones: this.api_urls.centers + 'center_timezones/',
        send_test_email_prefix: this.api_urls.centers,
        send_test_email_suffix: 'send_test_email/',
        deployment_settings: this.base_url + 'deployment_settings/',
      },
      users: {
        all: this.api_urls.users,
        add: this.api_urls.users,
        get: this.api_urls.users,
        edit: this.api_urls.users, // + userId
        delete: this.api_urls.users,
        signup: this.api_urls.users + 'signup/',
        login: this.api_urls.users + 'login/',
        logout: this.api_urls.users + 'logout/',
        roles: this.api_urls.users + 'roles/',
        is_authenticated: this.api_urls.users + 'is-authenticated/',
        authenticate: this.api_urls.users + 'authenticate/',
        email_exists: this.api_urls.users + 'email-exists/',
        active_station: this.api_urls.users + 'active-station/',
        active_station_set: this.api_urls.users + 'active-station/set',
        set_user_profile_picture: this.api_urls.users + 'set-user-profile-picture/',
      },
      vehicles: {
        get: this.api_urls.vehicles,
        add: this.api_urls.vehicles,
        edit: this.api_urls.vehicles,
        module_versions: this.api_urls.vehicles, // vehicleID
        allowable_package_weight_range: this.api_urls.vehicles + 'allowable_package_weight_range/',
        vehicles_maintenance: this.api_urls.vehicles_maintenance,
      },
      discrepancy_reports: {
        all: this.api_urls.discrepancy_reports,
        add: this.api_urls.discrepancy_reports,
        edit: this.api_urls.discrepancy_reports, // + report id
        resolve: this.api_urls.discrepancy_reports, // + (report id)/resolve/
      },
      checklists: {
        all: this.api_urls.checklists,
        get: this.api_urls.checklists, // + checklist id
        add: this.api_urls.checklists,
        edit: this.api_urls.checklists, // + checklist id
        delete: this.api_urls.checklists, // + checklist id
      },
      flightnetworks: {
        get_current: this.api_urls.flightnetworks + 'get_current/',
        get: this.api_urls.flightnetworks, // + networkId
        edit: this.api_urls.flightnetworks, // + networkId
      },
      part_numbers: {
        get: this.api_urls.part_numbers,
        add: this.api_urls.part_numbers,
        edit: this.api_urls.part_numbers, // + partNumberId
        delete: this.api_urls.part_numbers, // + partNumberId
      },
      history: {
        get: this.api_urls.history,
        csv_dump: this.api_urls.history + 'csv_dump/',
        filter: this.api_urls.history + 'filter/',
        payload_report: this.api_urls.history + 'payload_report/',
      },
      part_instances: {
        add: this.api_urls.part_instances,
        all: this.api_urls.part_instances,
        update: this.api_urls.part_instances, // + partInstanceId
        lookup_by_serial: this.api_urls.part_instances + 'lookup_by_serial/',
        unassigned_part_instances: this.api_urls.part_instances + 'unassigned_part_instances/',
        get_part_instance_log: this.api_urls.part_instances, // + partInstanceId
        update_part_instance_vehicle: this.api_urls.part_instances, // + partInstanceId
        unassign_part_from_vehicle: this.api_urls.part_instances, // + partInstanceId
      },
      manual: {
        add: this.api_urls.manual,
        load_manuals: this.api_urls.manual,
        delete: this.api_urls.manual, // manualId
        get_manual_by_id: this.api_urls.manual, // manualId
        update: this.api_urls.manual, // manualId
        manual_types: this.api_urls.manual + 'manual_types/',
        upload: this.api_urls.manual + 'upload/',
      },
      checklist_reports: {
        get: this.api_urls.checklist_reports,
        load_info: this.api_urls.checklist_reports, // auditLogId
        load_meta_data: this.api_urls.checklist_reports + 'filter_data/',
      },
      time_and_duty_reports: {
        get_dates: this.api_urls.time_and_duty_reports + 'get_dates/',
        get_report_for_current_user: this.api_urls.time_and_duty_reports + 'get_report_for_current_user/',
        get_report_for_user: this.api_urls.time_and_duty_reports + 'get_report_for_user/',
      },
    },
    colors: {
      routes: {
        green: '#50E3C2',
        purple: '#BD10E0',
        orange: '#F5A623',
        default: 'black',
      },
    },
    messages: {
      jrpc_service: {
        error: {
          ws_lost_connection: 'Temporarily lost connection to server',
        },
      },
    },
  } as const
}
